/**
 * Court Records dialog (Verification Summary): lists every court-record match
 * (records_found) as a card with its own "View Link" opening the court-order
 * url. Opened from the Court Records tile's "View" instead of jumping straight
 * to a single link. Reuses the shared VerificationModalShell so it matches the
 * ID / Income / Rent verification modals.
 *
 * Records can come from two sources (see utils/court-records.ts): the paid
 * OpenRoom pull (absent `source` on legacy data ⇒ OpenRoom) and the free
 * Ontario LTB open-data search. LTB hits carry no identity confirmation (the
 * dataset has no DOB), so their cards always say "verify against the order".
 */
import { FileText } from 'lucide-react'
import type { OpenroomRecordRef } from '#app/utils/application-detail-v2/types'
import { IconSquare, type PastelTone } from '../primitives'
import { VerificationModalShell } from './verification-bits'

/** Card tones cycled across the record cards (purple → rose → green), matching
 * the pastel tones used by the verification tiles. */
const RECORD_TONES: PastelTone[] = ['purple', 'rose', 'green']

/** Match-strength badge (team decision 2026-08-26: BOTH tiers are shown to
 * the landlord, visually distinct — possible always carries the verify
 * caveat because the LTB dataset has no date of birth). */
function strengthBadge(record: OpenroomRecordRef): {
	label: string
	className: string
	caveat?: string
} | null {
	if (record.source === 'ltb') {
		return record.matchStrength === 'strong'
			? {
					label: 'Strong match',
					className: 'bg-emerald-100 text-emerald-800',
					caveat: 'Name + address matched — verify against the order',
				}
			: {
					label: 'Possible match',
					className: 'bg-amber-100 text-amber-800',
					caveat: 'Name only — verify against the order',
				}
	}
	if (record.corroborated) {
		return {
			label: 'Corroborated',
			className: 'bg-emerald-100 text-emerald-800',
			caveat: 'Also found in the Ontario LTB feed',
		}
	}
	return null
}

export function CourtRecordsDialog({
	open,
	onOpenChange,
	records,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	records: OpenroomRecordRef[]
}) {
	return (
		<VerificationModalShell
			open={open}
			onOpenChange={onOpenChange}
			title="Court Records"
			verified={false}
		>
			<div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
				{records.map((record, index) => (
					<div
						key={record.url || index}
						className="flex flex-col items-center gap-3 rounded-2xl border border-[#F0F0F0] bg-[#FBFDFD] p-6 text-center"
					>
						<IconSquare tone={RECORD_TONES[index % RECORD_TONES.length]}>
							<FileText />
						</IconSquare>
						<span className="text-[19px] font-semibold text-[#32302F]">
							{`Record ${index + 1}`}
						</span>
						{record.url ? (
							<a
								href={record.url}
								target="_blank"
								rel="noreferrer"
								className="rounded-full bg-[#32302F] px-4 py-1 text-sm font-medium text-white transition-colors hover:bg-[#1a1918]"
							>
								View Link
							</a>
						) : (
							<span className="text-xs text-[#7A7876]">
								No document link available
							</span>
						)}
						{(() => {
							const badge = strengthBadge(record)
							if (!badge) return null
							return (
								<>
									<span
										className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.className}`}
									>
										{badge.label}
									</span>
									{badge.caveat ? (
										<span className="text-xs text-[#7A7876]">
											{badge.caveat}
										</span>
									) : null}
								</>
							)
						})()}
					</div>
				))}
			</div>
		</VerificationModalShell>
	)
}
